/* Copyright (C) nexleader - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written for nexleader <myipsat.com>, 2016-2026
 */

/**
 * PrintableArchetypeReportComponent
 *
 * Print-optimized layout of the IPSAT 2.0 Archetype Report. Structure
 * mirrors the canonical 13-section order from Steve's 2026-06-01
 * report_generation_boundaries.md (verified against the
 * IPSAT_2.0-Report-Sample-Final_Version.pdf designer reference and the
 * Chris Vacher PDF AI reference):
 *
 *   1.  Cover
 *   2.  Introduction
 *   3.  Integrating Your Identity Profile + Archetype Overview + Dynamic Pattern Table
 *   4.  Wheel + The Six IPSAT Archetypes
 *   5.  Why This Framework Is Unique + Identity Profile Summary table
 *   6.  Your Identity Statements (with Archetype Lens)
 *   7.  Integrated Insights
 *   8.  Counterintuitive Insights  — REMOVED 2026-08-15 (Steve); slot
 *       reserved, see the template comment for rationale
 *   9.  90-Day Development Focus
 *   10. Disclosure & Feedback Loop
 *   11. How [Primary] Interfaces with the Other Five
 *   12. Summary
 *   13. Appendix A — Original Identity Statements
 *
 * Data sources (per section):
 *   - Engine + stage:        archetypeReport.{archetype, ipsatScore, stage}
 *   - Static library:        archetypeReport.content.primary (+ .secondary)
 *   - AI personalization:    archetypeReport.aiContent.*
 *   - Prerequisites + raw statements: surveyResult.prerequisites + .questions
 *     (loaded alongside the report so the Identity Profile Summary table and
 *     Appendix A can render verbatim user inputs).
 */

import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { catchError, EMPTY, forkJoin } from 'rxjs';
import { ArchetypeReportService, ArchetypeReport } from '../../resources/archetype-report.service';
import { UserService } from '../../../../services/user.service';
import { SurveyResultService } from '../../../core/views/survey-result/survey-result.service';
import { ErrorHandlerService } from '../../../../services/error-handler.service';
import { ArchetypeWheelComponent } from '../../components/archetype-wheel/archetype-wheel.component';
import { ArchetypeIconComponent } from '../../components/archetype-icon/archetype-icon.component';

const ARCHETYPE_DISPLAY_NAMES: Record<string, string> = {
  'bold_initiator': 'Bold Initiator',
  'curious_explorer': 'Curious Explorer',
  'encouraging_connector': 'Encouraging Connector',
  'reflective_guide': 'Reflective Guide',
  'steady_builder': 'Steady Builder',
  'visionary_strategist': 'Visionary Strategist',
};

/**
 * Static descriptions used by section 4's six-archetype list. Copy sourced
 * verbatim from the IPSAT_2.0-Report-Sample-Final_Version.pdf designer
 * reference so all printed reports share identical framework wording.
 */
interface FrameworkArchetype { typeNumber: number; key: string; name: string; description: string; }
const FRAMEWORK_ARCHETYPES: FrameworkArchetype[] = [
  { typeNumber: 1, key: 'visionary_strategist', name: 'Visionary Strategist',
    description: 'Sees the big picture quickly and turns ideas into structured, achievable plans. Strategists synthesize complexity into clarity and lead with insight, logic, and purpose. They create alignment through systems thinking and long-range vision.' },
  { typeNumber: 2, key: 'bold_initiator', name: 'Bold Initiator',
    description: 'Turns vision into motion. Bold Initiators act decisively, embrace risk, and inspire movement when others hesitate. They bring catalytic energy and courage, mobilizing people to pursue new frontiers. Their challenge is balancing speed with sustainability.' },
  { typeNumber: 3, key: 'encouraging_connector', name: 'Encouraging Connector',
    description: 'Builds trust and belonging. Encouraging Connectors excel at bringing people together and creating relational momentum. They motivate through empathy and shared purpose, seeing potential in others and calling it out. Their growth edge is maintaining focus amid relational demands.' },
  { typeNumber: 4, key: 'steady_builder', name: 'Steady Builder',
    description: 'Creates stability that makes progress possible. Steady Builders value process, stewardship, and consistent execution. They turn vision into dependable systems and bring calm, methodical leadership to teams. Their challenge is embracing change and risk when conditions shift.' },
  { typeNumber: 5, key: 'reflective_guide', name: 'Reflective Guide',
    description: 'Brings wisdom through perspective. Reflective Guides listen deeply, ask discerning questions, and help others find clarity. They value authenticity, insight, and spiritual grounding. Their edge for growth is acting with decisive confidence once clarity emerges.' },
  { typeNumber: 6, key: 'curious_explorer', name: 'Curious Explorer',
    description: 'Discovers possibilities others overlook. Curious Explorers pursue learning, innovation, and new experiences that expand understanding. They thrive in discovery and creativity, connecting ideas across domains. Their growth edge is narrowing focus and bringing discoveries to practical fruition.' },
];

/**
 * Pairing of disclosure_prompts JSON keys to the display label + vulnerability
 * tier shown in the Disclosure & Feedback Loop section.
 */
const DISCLOSURE_AUDIENCES: { key: string; label: string; vulnerability: string }[] = [
  { key: 'spouse_partner', label: 'Spouse / Partner', vulnerability: 'High Vulnerability' },
  { key: 'close_friend', label: 'Close Friend', vulnerability: 'Medium / High Vulnerability' },
  { key: 'colleague_peer', label: 'Colleague / Peer', vulnerability: 'Medium Vulnerability' },
  { key: 'supervisor_mentor', label: 'Supervisor / Mentor', vulnerability: 'Medium / Low Vulnerability' },
  { key: 'direct_report_team_member', label: 'Direct Report / Team Member', vulnerability: 'Lower (Empowering)' },
];

@Component({
  selector: 'app-printable-archetype-report',
  standalone: true,
  imports: [CommonModule, RouterModule, ArchetypeWheelComponent, ArchetypeIconComponent],
  templateUrl: './printable-archetype-report.component.html',
  styleUrl: './printable-archetype-report.component.scss',
})
export class PrintableArchetypeReportComponent implements OnInit {
  archetypeReport: ArchetypeReport | null = null;
  user: any = null;
  surveyResult: any = null;
  isLoading = true;
  isDownloadingPdf = false;
  surveyResultId: string = '';

  archetypeDisplayNames = ARCHETYPE_DISPLAY_NAMES;
  frameworkArchetypes = FRAMEWORK_ARCHETYPES;
  disclosureAudiences = DISCLOSURE_AUDIENCES;

  constructor(
    private route: ActivatedRoute,
    private archetypeReportService: ArchetypeReportService,
    private userService: UserService,
    private surveyResultService: SurveyResultService,
    private errorHandlerService: ErrorHandlerService
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.params;
    const userId = params['user_id'];
    this.surveyResultId = params['survey_result_id'];

    forkJoin({
      report: this.archetypeReportService.getBySurveyResult(this.surveyResultId),
      user: this.userService.getUser(userId),
      surveyResult: this.surveyResultService.getSurveyResult(this.surveyResultId),
    })
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          this.isLoading = false;
          return EMPTY;
        })
      )
      .subscribe(({ report, user, surveyResult }) => {
        this.archetypeReport = report;
        this.user = user;
        this.surveyResult = surveyResult;
        this.isLoading = false;
      });
  }

  print(): void {
    window.print();
  }

  /**
   * Download the server-rendered PDF, which carries "Page N of M" page numbers
   * in the footer (Steve 2026-07-27). Browser print can't page-number this
   * free-flow report, so this is the way to get a numbered copy. The backend
   * spins up headless Chrome, so it's slower than window.print() — hence the
   * spinner state.
   */
  downloadPdf(): void {
    if (this.isDownloadingPdf) return;
    this.isDownloadingPdf = true;
    this.archetypeReportService.getPdf(this.surveyResultId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const last = (this.user?.lastName || 'Report').replace(/[^a-z0-9]+/gi, '-');
        a.download = `IPSAT-2.0-Archetype-Report-${last}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.isDownloadingPdf = false;
      },
      error: (error) => {
        this.isDownloadingPdf = false;
        this.errorHandlerService.handleError(error);
      },
    });
  }

  toArchetypeKey(name: string): string {
    return (name || '').toLowerCase().replace(/ /g, '_');
  }

  // ---- shortcut getters used throughout the template ----

  get primaryContent(): any { return this.archetypeReport?.content?.primary ?? null; }
  get overlayContent(): any { return this.archetypeReport?.content?.secondary ?? null; }
  get aiContent(): any { return (this.archetypeReport as any)?.aiContent ?? null; }
  get prereqs(): any { return this.surveyResult?.prerequisites ?? null; }
  get fullName(): string {
    if (!this.user) return '';
    const f = this.user.firstName || '';
    const l = this.user.lastName || '';
    return (f + ' ' + l).trim();
  }
  get firstName(): string { return this.user?.firstName || ''; }

  /**
   * Dynamic Pattern rows for section 3. Steve 2026-06-11 swap: prefer the
   * AI-generated paragraphs (Prompt 8 on aiContent.dynamic_pattern) over
   * the static per-archetype block from the v1.0 content library; static
   * is kept only as a fallback for reports generated before the swap.
   */
  get dynamicPatternRows(): { label: string; text: string }[] {
    const ai = (this.aiContent as any)?.dynamic_pattern || {};
    const fallback = this.primaryContent?.dynamic_pattern || {};
    const pick = (key: 'stress_recovery' | 'motivation_engagement' | 'conflict_style' | 'ideal_projects') =>
      ai[key] || fallback[key] || '';
    return [
      { label: 'Stress & Recovery',       text: pick('stress_recovery') },
      { label: 'Motivation & Engagement', text: pick('motivation_engagement') },
      { label: 'Conflict Style',          text: pick('conflict_style') },
      { label: 'Ideal Projects',          text: pick('ideal_projects') },
    ].filter(r => !!r.text);
  }
  get primaryArchetypeName(): string { return this.archetypeReport?.archetype?.primary || ''; }
  get overlayArchetypeName(): string { return this.archetypeReport?.archetype?.secondary || ''; }

  getFormattedDate(): string {
    if (!this.archetypeReport?.generatedAt) return '';
    return new Date(this.archetypeReport.generatedAt).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  }

  /**
   * "When you're at your best" → use the archetype's mature_expression from
   * the v1.0 library. Mirrors the designer-PDF's one-line at-your-best framing.
   */
  get atYourBestLine(): string {
    return this.primaryContent?.static_profile?.mature_expression || '';
  }

  /**
   * "Your greatest temptation" → first common_tension on the primary. The
   * designer PDF has a crafted line per archetype; using the first tension
   * is a faithful starting point. Replace with an AI-generated line later
   * if Steve wants more personalization here.
   */
  get greatestTemptationLine(): string {
    const t = this.primaryContent?.static_profile?.common_tensions || [];
    return t[0] || '';
  }

  /**
   * Top results for the Identity Profile Summary table.
   */
  get topMbti(): string { return this.prereqs?.mbti?.name || ''; }
  get topStrengths(): string[] {
    return (this.prereqs?.strengths || []).slice(0, 5).map((s: any) => s.name).filter(Boolean);
  }
  get topSkills(): string[] {
    return (this.prereqs?.skills || []).slice(0, 3).map((s: any) => s.name).filter(Boolean);
  }
  get topGiftsOrValues(): string[] {
    return (this.prereqs?.spiritualGifts || []).slice(0, 3).map((s: any) => s.name).filter(Boolean);
  }
  get topPassions(): string[] {
    return (this.prereqs?.passions || []).slice(0, 3).map((s: any) => s.name).filter(Boolean);
  }
  get categoryHeading(): string {
    return this.archetypeReport?.mode === 'values' ? 'Personal Values' : 'Spiritual Gifts';
  }

  /**
   * Archetype interaction rows for section 11. Filters out the primary
   * itself so the section lists only the other five.
   *
   * Prefers the AI-personalized rich cards from aiContent.archetype_interactions
   * (Prompt 7) when present; falls back to the static seed one-liner from the
   * content library for legacy reports.
   */
  get interactionRows(): {
    archetypeKey: string;
    archetypeName: string;
    description: string;
    rich?: { title: string; dynamic: string; best_practices: string; example: string; for_user: string };
  }[] {
    const interactions = this.primaryContent?.archetype_interactions || {};
    const aiInteractions = (this.aiContent && this.aiContent.archetype_interactions) || {};
    const primaryKey = this.toArchetypeKey(this.primaryArchetypeName);
    return Object.entries(interactions)
      .filter(([slug]) => slug !== primaryKey)
      .map(([slug, description]) => ({
        archetypeKey: slug,
        archetypeName: ARCHETYPE_DISPLAY_NAMES[slug] || slug,
        description: description as string,
        rich: aiInteractions[slug] || undefined,
      }))
      .filter(r => !!r.description || !!r.rich);
  }

  get hasRichInteractions(): boolean {
    return this.interactionRows.some(r => !!r.rich);
  }
}
